/**
 * Config health / drift panel (B3-b). Runs the Org Brain drift scanner on
 * demand and lists findings by severity. Surfaces each finding's suggested
 * fix. Read-only trigger; the one-click auto-fix lives behind the
 * supervisor+ /health/fix route and is intentionally not exposed here yet
 * (only `isolated_rule` is auto-fixable server-side).
 */

import { Button, Card, Spinner } from '@centraoneplus/ui';
import { HealthFinding, useOrgBrainHealth } from './hooks';

interface Props {
  tenantId: string;
}

const SEV_STYLES: Record<string, string> = {
  breaking: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  info: 'bg-sky-100 text-sky-800',
  none: 'bg-emerald-100 text-emerald-800',
};

// Plain-language labels — raw enum values like "breaking"/"none" aren't
// meaningful to an admin.
const SEV_LABEL: Record<string, string> = {
  breaking: 'Critical',
  warning: 'Needs attention',
  info: 'Info',
  none: 'No issues',
};

function SevPill({ severity }: { severity: string }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${
        SEV_STYLES[severity] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {SEV_LABEL[severity] ?? severity}
    </span>
  );
}

export default function OrgBrainHealth({ tenantId }: Props) {
  const health = useOrgBrainHealth(tenantId);
  const report = health.data;

  return (
    <Card className="p-6 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Config health check</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-xl">
            Scan your workspace for misconfigurations — empty pipelines,
            isolated rules, conflicting triggers, and orphaned references.
          </p>
        </div>
        <Button onClick={() => health.mutate()} disabled={health.isPending}>
          {health.isPending ? 'Scanning…' : 'Run health check'}
        </Button>
      </header>

      {health.isPending && <Spinner />}

      {report && !health.isPending && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Result:</span>
            <SevPill severity={report.overall_severity} />
            <span className="text-gray-500">
              · {report.findings.length}{' '}
              {report.findings.length === 1 ? 'issue' : 'issues'} found across{' '}
              {report.checks_run.length}{' '}
              {report.checks_run.length === 1 ? 'check' : 'checks'}
            </span>
          </div>

          {report.findings.length === 0 ? (
            <div className="text-sm text-emerald-800 bg-emerald-50 rounded px-3 py-2">
              No configuration issues detected. 🎉
            </div>
          ) : (
            <ul className="space-y-2">
              {report.findings.map((f: HealthFinding, i: number) => (
                <li
                  key={`${f.check}-${i}`}
                  className="border border-gray-200 rounded p-3"
                >
                  <div className="flex items-center gap-2">
                    <SevPill severity={f.severity} />
                    <span className="font-mono text-xs text-gray-500">
                      {f.check}
                    </span>
                  </div>
                  <div className="text-sm text-gray-900 mt-1">{f.message}</div>
                  {f.suggested_fix && (
                    <div className="text-xs text-gray-600 mt-1">
                      Suggested fix: {f.suggested_fix}
                    </div>
                  )}
                  {f.affected_nodes?.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      Affects:{' '}
                      {f.affected_nodes
                        .map((n) => `${n.type} ${n.natural_key}`)
                        .join(', ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {health.error && (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
          {(health.error as any)?.response?.data?.message ||
            (health.error as any)?.message ||
            'Health check failed'}
        </div>
      )}
    </Card>
  );
}